import { VStack } from '@chakra-ui/react';
import { useCallback, useEffect, useRef } from 'react';
import type { ImagePreviewRef } from '@halo/ui-chat-react';
import { ImagePreview, Resizable } from '@halo/ui-chat-react';
import { Outlet } from 'react-router';
import { useThemeContext } from '@halo/ui-react';
import { useAppStore } from '@halo/chat-sdk-react';
import { MainDialog } from '@/components/MainDialog';
import MediaDialogs from '@/components/MediaCall';
import { useOpenLink } from '@/scripts/hooks';
import { pickFileDialog } from '@/components/PickFileDialog';
import { useActionsStore } from '@/store/actions';
import LogReport from '@/components/LogReport';
import Nav from './NavBar';
import {
  PANELS_CONFIG,
  useAppResponsive,
  useNewMessagesListener,
  useRequestNotificationPermission,
} from './helpers';

export default function Main() {
  const { setThemeMode } = useThemeContext();
  const { hideNav, panelWidths, setPanelWidths } = useAppResponsive();

  useRequestNotificationPermission();
  useNewMessagesListener();

  useEffect(() => {
    // TODO: remove this after dark mode is supported
    setThemeMode('light' as any);
  }, [setThemeMode]);

  const onResize = useCallback(
    (widths: number[]) => {
      if (widths[0] < PANELS_CONFIG.PANELS_NAV_BAR_THRESHOLD_WIDTH) {
        widths[0] = PANELS_CONFIG.PANELS_NAV_BAR_MIN_WIDTH;
      }
      setPanelWidths(widths);
    },
    [setPanelWidths]
  );

  // handle not found
  const { openLink } = useOpenLink();
  const deepLinkRef = useRef<string>(null);
  const initTrigger = useAppStore((state) => state.initTrigger);

  useEffect(() => {
    const deepLink = sessionStorage.getItem('deepLink');
    if (!deepLink) return;
    sessionStorage.removeItem('deepLink');
    deepLinkRef.current = deepLink;
  }, []);

  useAsyncEffect(async () => {
    if (!initTrigger) return;
    const deepLink = deepLinkRef.current;
    if (!deepLink) return;
    deepLinkRef.current = null;
    await openLink(deepLink);
  }, [deepLinkRef, openLink, initTrigger]);

  // image preview ref
  const imagePreviewRef = useRef<ImagePreviewRef>(null);
  const setImagePreviewRef = useActionsStore((state) => state.setImagePreviewRef);
  useEffect(() => {
    setImagePreviewRef(imagePreviewRef);
  }, [setImagePreviewRef, imagePreviewRef]);

  return (
    <VStack className="Main" h="full" align="stretch" bg="bg.app">
      <Resizable.Root
        defaultWidths={hideNav ? [0] : panelWidths}
        minWidths={hideNav ? [0] : [PANELS_CONFIG.PANELS_NAV_BAR_MIN_WIDTH]}
        maxWidths={hideNav ? [0] : [PANELS_CONFIG.PANELS_NAV_BAR_MAX_WIDTH]}
        onResize={onResize}
      >
        <Resizable.Panel index={0}>
          <Nav
            hideNav={hideNav}
            isMiniNav={panelWidths[0] < PANELS_CONFIG.PANELS_NAV_BAR_THRESHOLD_WIDTH}
          />
        </Resizable.Panel>
        <Resizable.Handle index={0} />
        <Resizable.Panel index={1} overflow="initial" minW="0">
          <Outlet></Outlet>
        </Resizable.Panel>
      </Resizable.Root>
      {initTrigger && (
        <>
          <MainDialog />
          <MediaDialogs />
          {/*log report modal*/}
          <LogReport />
        </>
      )}
      <pickFileDialog.Viewport />
      <ImagePreview ref={imagePreviewRef} />
    </VStack>
  );
}
